import { Plus, X, DotsSixVertical } from '@phosphor-icons/react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'

import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import { reorder, getDefaultRoom, DEFAULT_LOCATION, DEFAULT_QUESTION } from '../helpers'

import RoomItem from './RoomItem'
import SelectedRoomHead from './SelectedRoomHead'

const SortableSidebarRoom = ({ room, index, isSelected, hasError, onClick, onRemove }) => {
  const { ref } = useSortable({ id: room.id, index })
  return (
    <div
      ref={ref}
      className={`sidebar-item ${isSelected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}
      onClick={onClick}
    >
      <div role="button" className="drag-handle">
        <DotsSixVertical className="dots" />
      </div>
      <span>{room.name}</span>
      <X className="cross" onClick={onRemove} />
    </div>
  )
}

const AdvancedSetupSection = ({ values, errors, setFieldValue = () => {} }) => {
  const { t } = useTranslation(['internal-inspections'])
  const [selectedRoomId, setSelectedRoomId] = useState('default')
  const [selectedLocationId, setSelectedLocationId] = useState('default')

  const { rooms = [] } = values

  const setRooms = (rooms) => setFieldValue('rooms', rooms)

  const getLocationError = (roomIndex, locationIndex, key) => {
    const roomError = get(errors, `rooms[${roomIndex}].roomItems`)

    if (roomError && typeof roomError === 'string') {
      return roomError
    }

    const roomItemsError = get(roomError, locationIndex)

    if (key) {
      return get(roomItemsError, key)
    } else if (typeof roomItemsError === 'string') {
      return roomItemsError
    }
  }

  const updateListPositions = (items) => {
    return items.map((item, index) => ({
      ...item,
      listPosition: index,
    }))
  }

  const onAddRoom = () => {
    const id = `room_${Date.now()}`
    const newRoom = { ...getDefaultRoom(t), id }
    setRooms(updateListPositions([...rooms, newRoom]))
    setSelectedRoomId(id)
  }

  const onAddLocation = () => {
    const id = `location_${Date.now()}`
    const roomIndex = rooms.findIndex((room) => room.id === selectedRoomId)
    const updatedRooms = [...rooms]
    const newLocation = {
      ...DEFAULT_LOCATION,
      id,
      listPosition: updatedRooms[roomIndex].roomItems.length,
    }
    updatedRooms[roomIndex] = {
      ...updatedRooms[roomIndex],
      roomItems: updateListPositions([...updatedRooms[roomIndex].roomItems, newLocation]),
    }
    setRooms(updatedRooms)
  }

  const onRemoveRoom = (roomId) => {
    const updatedRooms = updateListPositions(rooms.filter((room) => room.id !== roomId))
    setRooms(updatedRooms)
    setSelectedRoomId(null)
  }

  const onRemoveLocation = (locationId) => {
    const roomIndex = rooms.findIndex((room) => room.id === selectedRoomId)
    const updatedRooms = [...rooms]
    updatedRooms[roomIndex] = {
      ...updatedRooms[roomIndex],
      roomItems: updateListPositions(
        updatedRooms[roomIndex].roomItems.filter((location) => location.id !== locationId),
      ),
    }
    setRooms(updatedRooms)
  }

  const onEditRoom = (roomId, key, value) => {
    const roomIndex = rooms.findIndex((room) => room.id === roomId)
    const updatedRooms = [...rooms]
    updatedRooms[roomIndex] = { ...updatedRooms[roomIndex], [key]: value }
    setRooms(updatedRooms)
  }

  const onEditLocation = (locationId, key, value) => {
    const updatedRooms = rooms.map((room) => {
      if (room.id === selectedRoomId) {
        const updatedRoomItems = room.roomItems.map((item) => {
          if (item.id === locationId) {
            return { ...item, [key]: value }
          }
          return item
        })
        return { ...room, roomItems: updatedRoomItems }
      }
      return room
    })
    setRooms(updatedRooms)
  }

  const onRoomDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    setRooms(updateListPositions(reorder(rooms, sourceIndex, targetIndex)))
  }

  const onLocationDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    const roomIndex = rooms.findIndex((room) => room.id === selectedRoomId)
    const updatedRooms = [...rooms]
    updatedRooms[roomIndex] = {
      ...updatedRooms[roomIndex],
      roomItems: updateListPositions(
        reorder(updatedRooms[roomIndex].roomItems, sourceIndex, targetIndex),
      ),
    }
    setRooms(updatedRooms)
  }

  const onCollapse = (locationId) => {
    setSelectedLocationId(selectedLocationId === locationId ? null : locationId)
  }

  const onAddQuestion = () => {
    const id = `question_${Date.now()}`
    const updatedRooms = values.rooms.map((room) => {
      if (room.id === selectedRoomId) {
        return {
          ...room,
          roomItems: room.roomItems.map((item) => {
            if (item.id === selectedLocationId) {
              return {
                ...item,
                questions: [
                  ...item.questions,
                  {
                    ...DEFAULT_QUESTION,
                    id,
                  },
                ],
              }
            }
            return item
          }),
        }
      }
      return room
    })
    setRooms(updatedRooms)
  }

  const onRemoveQuestion = (questionId) => {
    const updatedRooms = rooms.map((room) => {
      if (room.id === selectedRoomId) {
        const updatedRoomItems = room.roomItems.map((location) => {
          if (location.id === selectedLocationId) {
            return {
              ...location,
              questions: location.questions.filter((q) => q.id !== questionId),
            }
          }
          return location
        })
        return { ...room, roomItems: updatedRoomItems }
      }
      return room
    })
    setRooms(updatedRooms)
  }

  const onChangeQuestion = (questionId, value) => {
    const updatedRooms = rooms.map((room) => {
      if (room.id === selectedRoomId) {
        const updatedRoomItems = room.roomItems.map((location) => {
          if (location.id === selectedLocationId) {
            return {
              ...location,
              questions: location.questions.map((q) =>
                q.id === questionId ? { ...q, question: value } : q,
              ),
            }
          }
          return location
        })
        return { ...room, roomItems: updatedRoomItems }
      }
      return room
    })

    setRooms(updatedRooms)
  }

  const toggleIsAdvanced = (value) => {
    setRooms([])
    setFieldValue('advanced', value)
    if (!value) {
      setFieldValue('guided', false)
    }
  }

  const toggleIsGuided = (value) => {
    setFieldValue('guided', value)
  }

  const selectedRoom = selectedRoomId ? rooms.find((room) => room.id === selectedRoomId) : null
  const selectedRoomIndex = rooms.findIndex((room) => room.id === selectedRoomId)

  return (
    <div className="advanced-setup-section">
      <div className="advanced-setup-section__head">
        <div>
          <span className="inspections-title">{t('internal-inspections:advancedSetup.title')}</span>
          <ToggleField
            name="setup-toggle"
            value={!!values.advanced}
            onChange={() => toggleIsAdvanced(!values.advanced)}
          />
          {!!errors.rooms && typeof errors.rooms === 'string' && (
            <span className="input-label--error">{errors.rooms}</span>
          )}
        </div>
        <ToggleField
          name="guided-toggle"
          label={t('internal-inspections:advancedSetup.guided')}
          value={values.guided}
          disabled={!values.advanced}
          onChange={() => toggleIsGuided(!values.guided)}
        />
      </div>
      {values.advanced ? (
        <React.Fragment>
          <div className="divider" />
          <div className="advanced-setup-section__content">
            <div className="advanced-setup-section__content__sidebar">
              <DragDropProvider onDragEnd={onRoomDragEnd}>
                <div>
                  {rooms.map((room, index) => (
                    <SortableSidebarRoom
                      key={room.id}
                      room={room}
                      index={index}
                      isSelected={room.id === selectedRoomId}
                      hasError={!!get(errors, `rooms[${index}]`)}
                      onClick={() => setSelectedRoomId(room.id)}
                      onRemove={(e) => {
                        e.stopPropagation()
                        onRemoveRoom(room.id)
                      }}
                    />
                  ))}
                </div>
              </DragDropProvider>
              <button className="button button-primary" onClick={onAddRoom}>
                <Plus />
                {t('internal-inspections:advancedSetup.addLocation')}
              </button>
            </div>
            <div className="advanced-setup-section__content__body">
              {selectedRoom ? (
                <React.Fragment>
                  <SelectedRoomHead
                    rooms={rooms}
                    selectedRoom={selectedRoom}
                    selectedRoomIndex={selectedRoomIndex}
                    selectedRoomId={selectedRoomId}
                    onEditRoom={onEditRoom}
                  />
                  <DragDropProvider onDragEnd={onLocationDragEnd}>
                    {selectedRoom.roomItems.map((location, index) => (
                      <RoomItem
                        key={location.id}
                        values={values}
                        room={location}
                        index={index}
                        selectedId={selectedLocationId}
                        selectedIndex={selectedRoomIndex}
                        onCollapse={onCollapse}
                        onEditLocation={onEditLocation}
                        onRemoveLocation={onRemoveLocation}
                        onAddQuestion={onAddQuestion}
                        onChangeQuestion={onChangeQuestion}
                        onRemoveQuestion={onRemoveQuestion}
                        getLocationError={getLocationError}
                      />
                    ))}
                  </DragDropProvider>
                  <div className="add-location" onClick={onAddLocation}>
                    <Plus />
                    <span>{t('internal-inspections:advancedSetup.addObject')}</span>
                  </div>
                  {!!getLocationError(selectedRoomIndex) && (
                    <span className="input-label--error">
                      {getLocationError(selectedRoomIndex)}
                    </span>
                  )}
                </React.Fragment>
              ) : null}
            </div>
          </div>
        </React.Fragment>
      ) : null}
    </div>
  )
}

AdvancedSetupSection.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  setFieldValue: PropTypes.func,
  defaultRoomId: PropTypes.number,
}

export default AdvancedSetupSection
